/**
 * DogIdCard (#581-followup) — the "click the dog's name → see the pet card"
 * modal from the 2026-07-21 owner walkthrough: "If you click on the name of
 * the dog, you see the picture… the pet card with all the information."
 *
 * Opens from any dog-first surface (dashboard arrivals/departures, admin
 * Bookings rows, Front Desk arrivals) via the shared DogFirstCell. Fetches
 * fresh on open (GET /api/admin/dogs/:dogId) so the day-of console always
 * shows current vax/meds, never a stale cached copy.
 *
 * Five states (DESIGN-PHILOSOPHY §Every component ships all five):
 *  - loading  → skeleton, never a blank flash;
 *  - error    → what happened + a Retry (cold-start safe via `request`'s
 *               backoff; a cross-tenant dog answers 404 → "not found");
 *  - empty    → a real dog with sparse data renders honest per-section empty
 *               copy ("No immunization records on file"), never invented data;
 *  - success  → the full card.
 *
 * Honesty (#546): age/sex/altered render ONLY when known — a null birthDate
 * shows no age, a null sex shows no sex line. We never guess.
 *
 * Accessibility: role="dialog" aria-modal, focus moves in on open and is
 * trapped, Esc + overlay-click close, the vax rows carry a text status mark
 * beside color (never color-only).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  careLogApi,
  fetchDogIdCard,
  humanizeApiError,
  type DogIdCard as DogIdCardData,
} from '../../lib/api.js';
import { CareStreakBadge } from '../care-log/CareStreakBadge.js';
import { AteNoneBadge, parseAteNone, streakRepeatsAteNone } from '../care-log/AteNoneBadge.js';
import type { AteNoneWarning } from '../../lib/api.js';
import { OwnerLink, canOpenOwner, ownerProfilePath, sessionRoles } from '../owner-link/index.js';
import {
  currentRowsFirst,
  vaxRowDisplay,
  VAX_ROW_LABEL,
  VAX_ROW_MARK,
} from '../dog-profile/format.js';
import {
  VACCINATION_STANDING_LABEL,
  vaccinationStanding,
} from '@ferro/core/modules/dog-profile/vaccination-source.js';

/** Ted #32a (E1.7, #1156) — "from Gingr", "entered by staff" or "unverified". */
function unsignedLabel(v: { verified: boolean; source?: string | null }): string {
  const label =
    VACCINATION_STANDING_LABEL[vaccinationStanding({ verified: v.verified, source: v.source ?? null }).kind];
  // Mid-sentence: lower-case the first letter only ("from Gingr").
  return label.charAt(0).toLowerCase() + label.slice(1);
}

interface DogIdCardProps {
  apiUrl: string;
  dogId: string;
  /** Fallback name shown in the header while the fetch is in flight. */
  fallbackName?: string;
  onClose: () => void;
}

/** Age in whole years/months from an ISO birthDate — null when not derivable. */
function deriveAge(birthDate: string | null, now: Date = new Date()): string | null {
  if (!birthDate) return null;
  const born = new Date(birthDate);
  if (Number.isNaN(born.getTime())) return null;
  let months =
    (now.getUTCFullYear() - born.getUTCFullYear()) * 12 +
    (now.getUTCMonth() - born.getUTCMonth());
  if (now.getUTCDate() < born.getUTCDate()) months -= 1;
  if (months < 0) return null; // future date — don't guess
  const years = Math.floor(months / 12);
  const rem = months % 12;
  if (years === 0 && rem === 0) return 'Under 1 mo';
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} yr${years === 1 ? '' : 's'}`);
  if (rem > 0) parts.push(`${rem} mo`);
  return parts.join(' ');
}

const SEX_WORD: Record<'male' | 'female', string> = {
  male: 'Male',
  female: 'Female',
};

const ALTERED_WORD: Record<
  NonNullable<DogIdCardData['altered']>,
  string
> = {
  intact: 'intact',
  spayed: 'spayed',
  neutered: 'neutered',
  altered: 'altered',
};

function formatDay(iso: string | null): string | null {
  if (!iso) return null;
  return iso.slice(0, 10);
}

function initialOf(name: string): string {
  const t = name.trim();
  return t.length > 0 ? t[0]!.toUpperCase() : '?';
}

export function DogIdCard({
  apiUrl,
  dogId,
  fallbackName,
  onClose,
}: DogIdCardProps): JSX.Element {
  const [dog, setDog] = useState<DogIdCardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // #721 — the refusal streak, so the person doing the check-in sees
  // "Ate none at dinner 2 days running" at the moment the owner is standing
  // there to be told. Null until (and unless) the read lands.
  const [streak, setStreak] = useState<{ label: string; days: number; slot: string } | null>(
    null,
  );
  // Sawyer #1295 — today's "Ate none" meals, from the same read.
  const [ateNone, setAteNone] = useState<AteNoneWarning | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    void fetchDogIdCard(apiUrl, dogId).then((res) => {
      if (cancelled) return;
      if (res.error || !res.data) {
        setError(humanizeApiError(res.error ?? 'not found'));
        setDog(null);
      } else {
        setDog(res.data.dog);
      }
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, dogId, reloadKey]);

  // #721 — the streak read is SECONDARY and deliberately independent of the
  // card's own load: a role without `pets:read:all` (or a transient failure)
  // must cost the badge, never the pet card. On any failure the badge simply
  // does not render — it only ever appears to raise an alarm, so silence is
  // the honest fallback rather than a reassuring "eating well" (§5).
  useEffect(() => {
    let cancelled = false;
    setStreak(null);
    setAteNone(null);
    void careLogApi.get(apiUrl, dogId, 30).then((res) => {
      if (cancelled || res.error || !res.data) return;
      setAteNone(parseAteNone(res.data.ateNone));
      const worst = res.data.worstStreak;
      if (!res.data.streakLabel || !worst) return;
      setStreak({ label: res.data.streakLabel, days: worst.days, slot: worst.slot });
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, dogId, reloadKey]);

  // Move focus into the dialog on open and return it to the invoking control
  // after every close path (button, Escape, backdrop, or route link).
  useEffect(() => {
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const t = window.setTimeout(() => closeRef.current?.focus(), 30);
    return () => {
      window.clearTimeout(t);
      restoreFocusRef.current?.focus();
    };
  }, []);

  // Esc closes; Tab is trapped within the dialog.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const root = dialogRef.current;
      if (!root) return;
      const focusable = root.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  const headerName = dog?.name ?? fallbackName ?? 'Dog';
  const age = dog ? deriveAge(dog.birthDate) : null;

  return (
    <div
      className="ferro-dogidcard"
      data-testid="dog-id-card"
    >
      <button
        type="button"
        aria-label="Close pet card"
        data-testid="dog-id-card-backdrop"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'default',
        }}
      />
      <div
        className="ferro-dogidcard__inner"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${headerName} — pet ID card`}
        onKeyDown={onKeyDown}
      >
        <button
          type="button"
          ref={closeRef}
          className="ferro-dogidcard__close"
          onClick={onClose}
          aria-label="Close pet card"
          data-testid="dog-id-card-close"
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {isLoading && (
          <div
            className="ferro-dogidcard__skeleton"
            data-testid="dog-id-card-loading"
            aria-busy="true"
          >
            <div className="ferro-dogidcard__skeleton-photo" />
            <div className="ferro-dogidcard__skeleton-line" />
            <div className="ferro-dogidcard__skeleton-line ferro-dogidcard__skeleton-line--short" />
          </div>
        )}

        {!isLoading && error && (
          <div className="ferro-dogidcard__error" role="alert" data-testid="dog-id-card-error">
            <p className="ferro-dogidcard__error-msg">{error}</p>
            <button
              type="button"
              className="ferro-dogidcard__retry"
              onClick={() => setReloadKey((k) => k + 1)}
            >
              Try again
            </button>
          </div>
        )}

        {!isLoading && !error && dog && (
          <div data-testid="dog-id-card-body">
            <header className="ferro-dogidcard__header">
              {dog.photoUrl ? (
                <img
                  className="ferro-dogidcard__photo"
                  src={dog.photoUrl}
                  alt={dog.name}
                />
              ) : (
                <span className="ferro-dogidcard__photo ferro-dogidcard__photo--initial" aria-hidden="true">
                  {initialOf(dog.name)}
                </span>
              )}
              <div className="ferro-dogidcard__ident">
                <h2 className="ferro-dogidcard__name">{dog.name}</h2>
                {dog.breed && <p className="ferro-dogidcard__breed">{dog.breed}</p>}
                <div className="ferro-dogidcard__chips">
                  {age && <span className="ferro-dogidcard__chip">{age}</span>}
                  {dog.sex && (
                    <span className="ferro-dogidcard__chip">
                      {SEX_WORD[dog.sex]}
                      {dog.altered ? `, ${ALTERED_WORD[dog.altered]}` : ''}
                    </span>
                  )}
                  {dog.sizeCategory && (
                    <span className="ferro-dogidcard__chip ferro-dogidcard__chip--muted">
                      {dog.sizeCategory}
                    </span>
                  )}
                  {typeof dog.weight === 'number' && (
                    <span className="ferro-dogidcard__chip ferro-dogidcard__chip--muted">
                      {dog.weight} lb
                    </span>
                  )}
                  {dog.temperament && (
                    <span className="ferro-dogidcard__chip ferro-dogidcard__chip--temperament">
                      {dog.temperament}
                    </span>
                  )}
                  {/* #721 — sits with the identity chips so it is above the
                      fold on the check-in card, not buried under vax. */}
                  {/* Sawyer #1295 — today's "Ate none" meals; the streak
                      only when it is about a different meal. */}
                  <AteNoneBadge ateNone={ateNone} dogName={dog.name} testId="dog-id-card-ate-none" />
                  {streakRepeatsAteNone(streak?.slot, ateNone) ? null : (
                    <CareStreakBadge
                      label={streak?.label ?? null}
                      days={streak?.days ?? 0}
                      dogName={dog.name}
                      testId="dog-id-card-care-streak"
                    />
                  )}
                </div>
              </div>
            </header>

            {/* Immunizations — the front-desk's #1 compliance check. */}
            <section className="ferro-dogidcard__section">
              <h3 className="ferro-dogidcard__section-title">Immunizations</h3>
              {dog.vaccinations.length === 0 ? (
                <p className="ferro-dogidcard__empty">No immunization records on file.</p>
              ) : (
                <ul className="ferro-dogidcard__vax-list">
                  {/* Ted #32a (#1156) — current records first; a record a
                      later one replaced reads "Replaced", never red. */}
                  {currentRowsFirst(dog.vaccinations).map((v) => {
                    const day = formatDay(v.expiresAt);
                    const shown = vaxRowDisplay(v);
                    return (
                      <li
                        key={v.id}
                        className={`ferro-dogidcard__vax ferro-dogidcard__vax--${shown}`}
                        data-testid={`dog-id-card-vax-${shown}`}
                      >
                        <span className="ferro-dogidcard__vax-mark" aria-hidden="true">
                          {VAX_ROW_MARK[shown]}
                        </span>
                        <span className="ferro-dogidcard__vax-name">{v.name}</span>
                        <span className="ferro-dogidcard__vax-meta">
                          {VAX_ROW_LABEL[shown]}
                          {day ? ` · exp ${day}` : ''}
                          {/* Ted #32a (E1.7) — an unsigned Gingr record says so;
                              "unverified" is left for records awaiting review. */}
                          {v.verified || shown === 'superseded' ? '' : ` · ${unsignedLabel(v)}`}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Active medications. */}
            {dog.medications.length > 0 && (
              <section className="ferro-dogidcard__section">
                <h3 className="ferro-dogidcard__section-title">Medications</h3>
                <ul className="ferro-dogidcard__med-list">
                  {dog.medications.map((m) => (
                    <li key={m.id} className="ferro-dogidcard__med">
                      <span className="ferro-dogidcard__med-name">{m.name}</span>
                      <span className="ferro-dogidcard__med-meta">
                        {[m.dosage, m.frequency, m.instructions]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Feeding. */}
            {(dog.feedingMethod || dog.foodType || dog.feedingNotes) && (
              <section className="ferro-dogidcard__section">
                <h3 className="ferro-dogidcard__section-title">Feeding</h3>
                <p className="ferro-dogidcard__text">
                  {[dog.feedingMethod, dog.foodType].filter(Boolean).join(' · ')}
                </p>
                {dog.feedingNotes && (
                  <p className="ferro-dogidcard__text ferro-dogidcard__text--muted">
                    {dog.feedingNotes}
                  </p>
                )}
              </section>
            )}

            {/* Care / allergies / special needs — safety-critical, shown when present. */}
            {(dog.allergies || dog.specialNeeds || dog.careInstructions) && (
              <section className="ferro-dogidcard__section">
                <h3 className="ferro-dogidcard__section-title">Care &amp; alerts</h3>
                {dog.allergies && (
                  <p className="ferro-dogidcard__text ferro-dogidcard__alert">
                    <strong>Allergies:</strong> {dog.allergies}
                  </p>
                )}
                {dog.specialNeeds && (
                  <p className="ferro-dogidcard__text ferro-dogidcard__alert">
                    <strong>Special needs:</strong> {dog.specialNeeds}
                  </p>
                )}
                {dog.careInstructions && (
                  <p className="ferro-dogidcard__text ferro-dogidcard__text--muted">
                    {dog.careInstructions}
                  </p>
                )}
              </section>
            )}

            {/* Vet — name, phone, email, address (2026-07-21 walkthrough:
                "you need the vet name and phone number and address"). Each
                line renders only when the data exists — no invented rows. */}
            {(dog.vetName || dog.vetPhone || dog.vetEmail || dog.vetAddress) && (
              <section
                className="ferro-dogidcard__section"
                data-testid="idcard-vet"
              >
                <h3 className="ferro-dogidcard__section-title">Veterinarian</h3>
                {dog.vetName && (
                  <p className="ferro-dogidcard__text ferro-dogidcard__vet-name">
                    {dog.vetName}
                  </p>
                )}
                {(dog.vetPhone || dog.vetEmail) && (
                  <p className="ferro-dogidcard__text">
                    {dog.vetPhone && (
                      <a className="ferro-dogidcard__tel" href={`tel:${dog.vetPhone}`}>
                        {dog.vetPhone}
                      </a>
                    )}
                    {dog.vetPhone && dog.vetEmail ? ' · ' : ''}
                    {dog.vetEmail && (
                      <a
                        className="ferro-dogidcard__tel"
                        href={`mailto:${dog.vetEmail}`}
                      >
                        {dog.vetEmail}
                      </a>
                    )}
                  </p>
                )}
                {dog.vetAddress && (
                  <p className="ferro-dogidcard__text ferro-dogidcard__vet-address">
                    {dog.vetAddress}
                  </p>
                )}
              </section>
            )}

            {/* Footer — the modal is a glance; the dog's FULL record lives on
                its own page (#553). "Dog profile →" is the primary CTA and
                routes to the pet page, where vaccination records are reviewed;
                the owner (with a one-tap call) is the secondary "Owner
                record →". Ted #32 item 8 (#1156): the old "Full record →"
                label read as part of "Owner record", so he never found the
                dog's page — the label names its destination. It renders even
                when the owner is unknown — dogId is always available. */}
            <footer className="ferro-dogidcard__footer">
              {dog.owner && (
                <div className="ferro-dogidcard__owner">
                  {/* Sawyer 01 owner-links (F2) — the owner's name opens the client profile
                      (and closes the card) for desk roles; text otherwise. */}
                  <OwnerLink
                    className="ferro-dogidcard__owner-name"
                    name={`${dog.owner.firstName} ${dog.owner.lastName}`.trim()}
                    customerId={dog.owner.customerId}
                    onNavigate={onClose}
                    testId="dog-id-card-owner-name"
                  />
                  <a
                    className="ferro-dogidcard__tel"
                    href={`tel:${dog.owner.phone}`}
                    data-testid="dog-id-card-owner-tel"
                  >
                    {dog.owner.phone}
                  </a>
                </div>
              )}
              <div className="ferro-dogidcard__footer-actions">
                <Link
                  to={`/dogs/${dogId}`}
                  className="ferro-dogidcard__fullrecord"
                  onClick={onClose}
                  data-testid="dog-id-card-full-record"
                >
                  Dog profile →
                </Link>
                {/* Sawyer 01 owner-links (F2) — gated like the owner name: a floor role never
                    gets a button that ends at "Access restricted". */}
                {dog.owner && canOpenOwner(sessionRoles(), dog.owner.customerId) && (
                  <Link
                    to={ownerProfilePath(dog.owner.customerId)}
                    className="ferro-dogidcard__ownerrecord"
                    onClick={onClose}
                    data-testid="dog-id-card-owner-record"
                  >
                    Owner record →
                  </Link>
                )}
              </div>
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}
